/**
 * TieredDashboardView - Composes the right tier-specific dashboard
 * organism (SuperAdminDashboard or ManagerDashboard) with the shared
 * useDashboardMetrics hook.
 *
 * Lives next to DashboardPage so the dispatch + fetch wiring stays
 * close to the page entry point. The actual UI lives in the two
 * organism components — this file is a thin shell that picks the
 * right one and feeds it data.
 *
 * Tech-tier users never reach this component — DashboardPage routes
 * them to the existing simpler dashboard rendered below the dispatch.
 */
import React from 'react';
import { useDashboardMetrics } from '../../hooks/useDashboardMetrics';
import { SuperAdminDashboard, ManagerDashboard, DashboardTaskList } from '../../components/organisms';
import { Alert } from '../../components/atoms';
import { PageContainer } from '../../components/templates/PageContainer';

/* ============================================
 * TYPE DEFINITIONS
 * ============================================ */

interface TieredDashboardViewProps {
    /** Pre-resolved tier — caller already checked the user's roles. */
    tier: 'super_admin' | 'manager';
}

/* ============================================
 * COMPONENT
 * ============================================ */

export const TieredDashboardView: React.FC<TieredDashboardViewProps> = ({ tier }) => {
    /* Single hook owns filter state + metrics fetch. Both tier
     * organisms consume the same shape so we can pick at render. */
    const { metrics, filters, setFilters, isLoading, error } = useDashboardMetrics();

    return (
        <PageContainer block="tiered-dashboard-view" dataRef="page-tiered-dashboard-view" width="wide">
            {/* Page header */}
            <div className="mb-16px">
                <h1 className="text-24px tablet:text-28px font-semibold text-brand-text">Dashboard</h1>
                <p className="text-14px text-brand-text-muted mt-4px">
                    {tier === 'super_admin'
                        ? 'Platform-wide work orders, inspections, revenue, and team workload.'
                        : 'Your work, your team, your company.'}
                </p>
            </div>

            {/* Loading + error banners — kept separate from the body
             * render so the cards still mount once data arrives. */}
            {isLoading && !metrics && (
                <div className="text-13px text-brand-text-muted py-8px">Loading metrics…</div>
            )}
            {error && (
                <Alert variant="error" className="mb-12px">{error}</Alert>
            )}

            {/* Tier-specific body */}
            {metrics && tier === 'super_admin' && (
                <SuperAdminDashboard metrics={metrics} filters={filters} onFiltersChange={setFilters} />
            )}
            {metrics && tier === 'manager' && (
                <ManagerDashboard metrics={metrics} filters={filters} onFiltersChange={setFilters} />
            )}

            {/* Today + yesterday task list — independent of the metrics
             * fetch, so it renders (with its own skeleton) even while the
             * metric cards are still loading or errored. */}
            <DashboardTaskList className="mt-24px" />
        </PageContainer>
    );
};

export default TieredDashboardView;
